import { useEffect, useRef, useState } from "react";
import { kurzText, langText, mitGewaehlten, umschalten } from "../kern/filterwahl.js";
import { t } from "../i18n/index.js";

/* ================================================================
   Auswahlfeld mit Mehrfachwahl für die Filterleisten.

   Sieht zu wie ein <select> aus und sitzt an seiner Stelle, öffnet
   aber eine Liste mit Häkchen, die beim Anklicken offen bleibt — man
   wählt 2019, 2020 und 2021 nacheinander, ohne jedes Mal neu
   aufzuklappen. Ein natives <select multiple> wäre auf dem Telefon
   brauchbar, am Rechner aber ein Listenkasten, der Strg-Klick
   verlangt und den die Filterleiste nicht unterbringt.

   Die oberste Zeile „Alle …" hebt die Wahl auf; sie ist angehakt,
   solange nichts gewählt ist, und zeigt damit zugleich, was der
   leere Zustand bedeutet.

   Bei langen Listen (Winzer, Rebsorten) kommt ein Suchfeld dazu.
   Gewählte Werte stehen immer in der Liste, auch wenn ein anderer
   Filter sie gerade ausschließt — siehe mitGewaehlten().

   Optionen: [{ wert, label }]. `beschrifte` liefert den Text für
   einen gewählten Wert, der in `optionen` fehlt.
   ================================================================ */

const SUCHE_AB = 9;

function MehrfachAuswahl({ wahl, setWahl, optionen, alleText, beschriftung, beschrifte, className = "" }) {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState("");
  const huelle = useRef(null);
  const knopf = useRef(null);
  const suchfeld = useRef(null);

  const liste = mitGewaehlten(optionen, wahl, beschrifte);
  const q = suche.trim().toLowerCase();
  const treffer = q ? liste.filter((o) => String(o.label).toLowerCase().includes(q)) : liste;
  const suchbar = liste.length >= SUCHE_AB;
  const aktiv = wahl.length > 0;

  const schliesse = (fokusZurueck = false) => {
    setOffen(false); setSuche("");
    if (fokusZurueck && knopf.current) knopf.current.focus();
  };

  /* Klick oder Berührung außerhalb schließt. Touch eigens, weil
     mousedown auf dem Telefon erst nach dem Loslassen kommt. */
  useEffect(() => {
    if (!offen) return;
    const zu = (e) => { if (huelle.current && !huelle.current.contains(e.target)) schliesse(); };
    document.addEventListener("mousedown", zu);
    document.addEventListener("touchstart", zu);
    return () => {
      document.removeEventListener("mousedown", zu);
      document.removeEventListener("touchstart", zu);
    };
  }, [offen]);

  /* Suchfeld nur am Rechner gleich fokussieren — auf dem Telefon
     schöbe die Tastatur die halbe Liste aus dem Bild. */
  useEffect(() => {
    if (!offen || !suchbar || !suchfeld.current) return;
    if (window.matchMedia && window.matchMedia("(pointer: fine)").matches) suchfeld.current.focus();
  }, [offen, suchbar]);

  const taste = (e) => { if (e.key === "Escape" && offen) { e.preventDefault(); e.stopPropagation(); schliesse(true); } };

  /* Wer mit Tab aus der Liste herausläuft, will weiter — dann zu. */
  const fokusWeg = (e) => {
    if (offen && huelle.current && e.relatedTarget && !huelle.current.contains(e.relatedTarget)) schliesse();
  };

  const text = kurzText(wahl, liste, alleText);

  return (
    <div className={"wl-mehrfach" + (className ? " " + className : "")} ref={huelle} onKeyDown={taste} onBlur={fokusWeg}>
      <button type="button" ref={knopf}
        className={"wl-eingabe wl-mehrfach-knopf" + (aktiv ? " ist-aktiv" : "")}
        aria-haspopup="true" aria-expanded={offen}
        aria-label={`${beschriftung}: ${langText(wahl, liste, alleText)}`}
        title={langText(wahl, liste, alleText)}
        onClick={() => (offen ? schliesse() : setOffen(true))}>
        <span className="wl-mehrfach-text">{text}</span>
        <span className="wl-mehrfach-pfeil" aria-hidden="true">▾</span>
      </button>
      {offen && (
        <div className="wl-mehrfach-liste" role="group" aria-label={beschriftung}>
          {suchbar && (
            <div className="wl-mehrfach-suchzeile">
              <input ref={suchfeld} className="wl-eingabe wl-mehrfach-suche" value={suche}
                onChange={(e) => setSuche(e.target.value)} placeholder={t("Suchen …")}
                aria-label={t("Suchen …")} autoComplete="off" />
            </div>
          )}
          <label className={"wl-mehrfach-punkt ist-alle" + (aktiv ? "" : " ist-gewaehlt")}>
            <input type="checkbox" checked={!aktiv} onChange={() => setWahl([])} />
            <span>{alleText}</span>
          </label>
          {treffer.map((o) => {
            const an = wahl.includes(String(o.wert));
            return (
              <label key={o.wert} className={"wl-mehrfach-punkt" + (an ? " ist-gewaehlt" : "")}>
                <input type="checkbox" checked={an} onChange={() => setWahl(umschalten(wahl, o.wert))} />
                <span>{o.label}</span>
              </label>
            );
          })}
          {q && treffer.length === 0 && <div className="wl-mehrfach-leer">{t("Keine Treffer")}</div>}
        </div>
      )}
    </div>
  );
}

export { MehrfachAuswahl };
